import React, { Suspense, useEffect } from 'react';

import { useModal } from 'context/ModalContext';
import { useDeclineWelcomeCoupon, useWelcomeCoupon } from 'entities/coupon';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { lazyWithRetry } from 'shared/lib/lazyRetry';
import { toast } from 'sonner';
import { useInfoStore } from 'store/useInfoStore';

// 모달은 열릴 때만 렌더되므로 코드도 열릴 때 내려받는다 — 로그인 트리가 엔트리 번들에서 빠짐
const FindEmailModal = lazyWithRetry(() => import('pages/login/FindEmailModal'));
const LoginModal = lazyWithRetry(() => import('pages/login/LoginModal'));
const OnboardingModal = lazyWithRetry(() => import('pages/login/OnboardingModal'));
const PasswordModal = lazyWithRetry(() => import('pages/login/PasswordModal'));
const ResetModal = lazyWithRetry(() => import('pages/login/ResetModal'));
const SignupModal = lazyWithRetry(() => import('pages/login/SignupModal'));

const Modals = () => {
  const { modalType, openModal, closeModal, closeModalWithRedirectClear, MODAL_TYPES } = useModal();

  const { eligible } = useWelcomeCoupon();
  const { mutateAsync: decline, isPending } = useDeclineWelcomeCoupon();
  const { profileCompleted } = useInfoStore();
  const { t } = useTranslation('auth');
  // 새로고침은 최초 가입 혜택을 소멸시키지 않는다. 재로그인은 서버에서 대상 종료.
  useEffect(() => {
    if (eligible && !profileCompleted && modalType === MODAL_TYPES.NONE) openModal(MODAL_TYPES.ONBOARDING);
  }, [eligible, profileCompleted, modalType, openModal, MODAL_TYPES.NONE, MODAL_TYPES.ONBOARDING]);

  const handleOnboardingClose = async () => {
    if (isPending) return;
    try {
      if (!profileCompleted) await decline();
      closeModalWithRedirectClear();
    } catch {
      toast.error(t('onboarding.saveError'));
    }
  };

  if (modalType === MODAL_TYPES.NONE) return null;

  // 로그인 모달은 redirect 까지 같이 정리해야 해서 closeModalWithRedirectClear 사용
  const handleBackdropClose = () => {
    if (modalType === MODAL_TYPES.ONBOARDING) {
      void handleOnboardingClose();
    } else if (modalType === MODAL_TYPES.LOGIN) {
      closeModalWithRedirectClear();
    } else {
      closeModal();
    }
  };

  const renderModal = () => {
    switch (modalType) {
      case MODAL_TYPES.LOGIN:
        return <LoginModal onClose={closeModalWithRedirectClear} />;
      case MODAL_TYPES.SIGNUP:
        return <SignupModal onClose={closeModal} />;
      case MODAL_TYPES.ONBOARDING:
        return <OnboardingModal onClose={handleOnboardingClose} />;
      case MODAL_TYPES.FIND_EMAIL:
        return <FindEmailModal onClose={closeModal} />;
      case MODAL_TYPES.PASSWORD:
        return <PasswordModal onClose={closeModal} />;
      case MODAL_TYPES.RESET:
        return <ResetModal onClose={closeModal} />;
      default:
        return null;
    }
  };

  return ReactDOM.createPortal(
    <div
      className={
        modalType === MODAL_TYPES.ONBOARDING
          ? 'fixed inset-0 z-50 flex h-[100dvh] items-center justify-center bg-black/50 px-3 pb-20 pt-6 min-[800px]:py-6'
          : 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 pb-20 min-[800px]:pb-0 md:px-0'
      }
      onClick={handleBackdropClose}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Suspense fallback={null}>{renderModal()}</Suspense>
      </div>
    </div>,
    document.getElementById('root') as HTMLElement,
  );
};

export default Modals;
